import styled, { CSSObject } from '@emotion/styled';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { QcsLoadingButton } from '../../common/basic/QcsLoadingButton';
import { SlideList } from './SlideList';
import { useAppDispatch, useAppSelector } from '../../../store';
import { getTrainingSlides, selectTrainingSlides, selectTrainingSlidesState, trainingSlidesActions } from '../../../store/entities/trainingSlides';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { HtmlEditor } from '../../common/html/HtmlEditor';
import { FetchState } from '../../../store/fetchState';
import { QcsTextField } from '../../common/basic/QcsTextField';
import { Loader } from '../../common/Loader';
import { SaveError } from '../../common/SaveError';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { selectTrainingVersion } from '../../../store/entities/trainingVersion';
import { SlideDto } from '@api/models/training/SlideDto';
import { trainingVersionService } from '@api/services/services';
import { resources } from '@api/resources';
import { useResourceLoading } from '@api/utils/ApiLoadingStore';
import { doNothing } from '../../../utils/shared';

const ContentContainer = styled.div(
  (): CSSObject => ({
    padding: '1rem',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  }),
);

const HeaderContainer = styled.div(
  (): CSSObject => ({
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-start',
  }),
);

const CornerBlock = styled.div(
  (): CSSObject => ({
    marginLeft: 'auto',
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-start',
  }),
);

const EditorContainer = styled.div(
  ({ theme }): CSSObject => ({
    height: '100vh',
    width: '100vw',
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: theme.zIndex.modal,
    display: 'grid',
    gridTemplateColumns: '1fr 4fr',
    overflow: 'auto',
    backgroundColor: theme.palette.background.default,
  }),
);

const ControlButtonsContainer = styled.div(
  ({ theme }): CSSObject => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginTop: '1rem',
    gap: '1rem',
    flexDirection: 'column',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
  }),
);

const SaveButtonContainer = styled.div(
  (): CSSObject => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '1rem',
  }),
);

export const TrainingSlidesEditor: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trainingId, trainingVersionId, languageCode } = useParams();

  const editorGetHtml = useRef<(() => string) | null>(null);
  const editorReloadHtml = useRef<(() => void) | null>(null);
  const [editorSetFocus, setEditorSetFocus] = useState(true);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const trainingSlides = useAppSelector(selectTrainingSlides) as SlideDto[] | undefined;
  const trainingSlidesState = useAppSelector(selectTrainingSlidesState);

  const [unsavedChangesFunction, setUnsavedChangesFunction] = useState<(() => void) | null>(null);
  const [confirmDeleteFunction, setConfirmDeleteFunction] = useState<(() => void) | null>(null);

  const allSlideResources = [resources.updateSlide, resources.changeSlideOrder, resources.createSlide, resources.deleteSlide];
  const saving = useResourceLoading(allSlideResources);

  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(true);
  const [missingTitleError, setMissingTitleError] = useState(false);
  const trainingVersion = useAppSelector(selectTrainingVersion);
  const editable = trainingVersion.editable;

  useEffect(() => {
    dispatch(getTrainingSlides(trainingVersionId!, languageCode!));
    setLoading(false);
  }, [dispatch, languageCode, trainingVersionId]);

  const [selectedSlideId, setSelectedSlideId] = useState<string | null>(null);

  useEffect(() => {
    if (initializing && !loading && trainingSlidesState === FetchState.Loaded) {
      setInitializing(false);
      setSelectedSlideId(trainingSlides?.[0]?.id ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainingSlidesState, loading]);

  const selectedSlide: SlideDto | undefined = useMemo(
    () => trainingSlides?.find((slide) => slide.id === selectedSlideId),
    [selectedSlideId, trainingSlides],
  );

  const [title, setTitle] = useState('');

  useEffect(() => {
    setTitle(selectedSlide?.title ?? '');
    setMissingTitleError(false);
    editorReloadHtml.current?.();
    setEditorSetFocus(true);
  }, [selectedSlide]);

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(event.target.value);
  };

  const handleSaveSlide = async () => {
    setMissingTitleError(false);

    if (!title || title.trim() === '') {
      setMissingTitleError(true);
      throw new Error('missingTitle');
    }

    const content = editorGetHtml.current?.();
    const data = {
      ...selectedSlide!,
      title: title,
      content: content ?? '',
    };

    await trainingVersionService.updateSlide(selectedSlideId!, data);
    dispatch(getTrainingSlides(trainingVersionId!, languageCode!));
  };

  const handleDeleteSlide = async () => {
    const newSlides = trainingSlides?.filter((slide) => slide.id !== selectedSlideId);
    dispatch(trainingSlidesActions.success(newSlides ?? []));

    await trainingVersionService.deleteSlide(selectedSlideId!);
  };

  const handleAddSlide = async () => {
    const newSlide = {
      id: '',
      title: t('training.newSlide'),
      content: '',
      language: {
        code: languageCode!,
        flag: '',
        name: '',
      },
    };

    const newSlideResponse = await trainingVersionService.createSlide(trainingVersionId!, newSlide);

    dispatch(trainingSlidesActions.success([...(trainingSlides || []), { ...newSlide, id: newSlideResponse.data.id! }]));

    setSelectedSlideId(newSlideResponse.data.id!);
    titleInputRef.current?.focus();
    titleInputRef.current?.select();
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const slidesRollback = trainingSlides;

    const oldIndex = trainingSlides!.findIndex((slide) => slide.id === active.id);
    const newIndex = trainingSlides!.findIndex((slide) => slide.id === over.id);

    const result = arrayMove(trainingSlides!, oldIndex, newIndex);

    dispatch(trainingSlidesActions.success(result));

    try {
      await trainingVersionService.changeSlideOrder(trainingVersionId!, languageCode!, {
        entries: result.map((slide) => slide.id!),
      });
    } catch {
      dispatch(trainingSlidesActions.success(slidesRollback ?? []));
    }
  };

  const handleSelectSlide = (id: string) => {
    setSelectedSlideId(id);
  };

  const handleClose = () => {
    navigate(`/training/${trainingId}/${trainingVersionId}`);
  };

  const checkChanges =
    (f: (...params: any) => any) =>
    (...params: any) => {
      const htmlChanged =
        (selectedSlide && editorGetHtml.current?.() !== selectedSlide?.content?.replace(/\n|\t/g, '').replace(/>\s+</g, '><')) ?? '';
      const titleChanged = selectedSlide && title !== selectedSlide?.title;

      if (editable && (htmlChanged || titleChanged)) {
        setUnsavedChangesFunction(() => () => f(...params));
      } else {
        f(...params);
      }
    };

  const confirmDeleteSlide =
    (f: (...params: any) => any) =>
    (...params: any) => {
      setConfirmDeleteFunction(() => () => f(...params));
    };

  if (initializing || !trainingSlides) {
    return <Loader />;
  }
  return (
    <>
      <UnsavedChangesDialog onClose={() => setUnsavedChangesFunction(null)} onConfirm={unsavedChangesFunction} onSaveSlide={handleSaveSlide} />
      <ConfirmDialog
        onClose={() => setConfirmDeleteFunction(null)}
        onConfirm={confirmDeleteFunction}
        title={t('training.confirmDeleteDialog.title')}
        message={t('training.confirmDeleteDialog.message')}
      />

      <EditorContainer>
        <SlideList
          slides={trainingSlides}
          selectedId={selectedSlide?.id}
          onItemClick={checkChanges(handleSelectSlide)}
          onDragEnd={handleDragEnd}
          addSlide={checkChanges(handleAddSlide)}
          isDragDisabled={saving || !editable}
          onCloseEditor={checkChanges(handleClose)}
        />

        <ContentContainer>
          <HeaderContainer>
            {selectedSlide && (
              <QcsTextField
                inputRef={titleInputRef}
                sx={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  maxWidth: '25rem',
                }}
                label={t('training.slideTitle')}
                value={title}
                variant="standard"
                onChange={handleTitleChange}
                disabled={!editable}
                fullWidth={true}
              />
            )}

            <CornerBlock>
              <QcsLoadingButton
                variant="outlined"
                color="error"
                resourceLoading={allSlideResources}
                onClick={confirmDeleteSlide(handleDeleteSlide)}
                disabled={!editable}
              >
                {t('training.deleteSlide')}
              </QcsLoadingButton>
            </CornerBlock>
          </HeaderContainer>

          {selectedSlide && (
            <div>
              <HtmlEditor
                html={selectedSlide?.content ?? ''}
                getHtmlRef={editorGetHtml}
                reloadHtmlRef={editorReloadHtml}
                allowSelectRisks={true}
                setFocusValue={editorSetFocus}
                setFocusFunc={setEditorSetFocus}
              />

              <ControlButtonsContainer>
                <SaveButtonContainer>
                  {missingTitleError && <SaveError error={t('training.missingTitleError')} />}
                  <QcsLoadingButton
                    variant="contained"
                    resourceLoading={allSlideResources}
                    onClick={() => { handleSaveSlide().catch(() => {doNothing()}); }}
                    disabled={!editable}
                  >
                    {t('training.saveSlide')}
                  </QcsLoadingButton>
                </SaveButtonContainer>
              </ControlButtonsContainer>
            </div>
          )}
        </ContentContainer>
      </EditorContainer>
    </>
  );
};
